import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { useLanguage } from "context/LanguageContext";
import { useNotification } from "context/NotificationContext";
import { handleNavigateBack, renderRangeField, validateFields, renderCheckboxGroup, renderSelectField } from "components/formUtils";

const EscoresForm = () => {
  const { GetTranslation } = useLanguage();
  const notify = useNotification();
  const navigate = useNavigate();
  const { id } = useParams();

  const [formData, setFormData] = useState({
    nyha: [],
    ccs: [],
    euroscore: "",
    sts_score: "",
    frail_score: ""
  });

  const token = localStorage.getItem("token");

  // Função para lidar com checkbox (NYHA e CCS)
  const handleCheckboxChange = (e, field) => {
    const { value, checked } = e.target;
    setFormData((prev) => {
      const updatedValues = checked
        ? [...prev[field], value] // Adiciona se estiver checado
        : prev[field].filter((v) => v !== value); // Remove se desmarcado
      return { ...prev, [field]: updatedValues };
    });
  };

  // Carrega os dados ao editar
  useEffect(() => {
    if (id) {
      fetch(`${process.env.REACT_APP_API_URL}/escores/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        }
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.success) {
            setFormData(data.data);
          } else {
            notify({
              type: "error",
              message: data.error || "Erro ao buscar os dados"
            });
          }
        })
        .catch(() => {
          notify({
            type: "error",
            message: "Erro ao buscar os dados"
          });
        });
    }
  }, [id, token, notify]);

  const handleSubmit = () => {
    const requiredFields = ["nyha", "ccs", "euroscore", "sts_score", "frail_score"];
    if (!validateFields(formData, requiredFields, GetTranslation, notify)) return;

    const apiUrl = id
      ? `${process.env.REACT_APP_API_URL}/escores/update/${id}`
      : `${process.env.REACT_APP_API_URL}/escores/add`;

    const method = id ? "PUT" : "POST";

    fetch(apiUrl, {
      method: method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(formData)
    })
      .then((response) => {
        if (response.ok) {
          notify({
            type: "success",
            message: id ? "Escore atualizado com sucesso!" : "Escore adicionado com sucesso!"
          });
        } else {
          response.json().then((data) => {
            notify({
              type: "error",
              message: `Erro: ${data.error}`
            });
          });
        }
      })
      .catch(() => {
        notify({
          type: "error",
          message: "Erro ao enviar os dados"
        });
      });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const confirmSave = (e) => {
    e.preventDefault();
    confirmDialog({
      message: GetTranslation("Confirmar_salvar"),
      header: GetTranslation("Confirmar"),
      icon: "pi pi-exclamation-triangle",
      acceptLabel: GetTranslation("Yes"),
      rejectLabel: GetTranslation("No"),
      accept: handleSubmit,
      reject: () => {
        notify({
          type: "info",
          message: GetTranslation("Operacao_cancelada")
        });
      }
    });
  };

  const handleBack = handleNavigateBack(navigate, id);


  return (
    <div className="card p-3">
      <ConfirmDialog />
      <h4>{id ? GetTranslation("Edit_Escore") : GetTranslation("Add_Escore")}</h4>
      <form className="row g-3">
        {/* NYHA (Checkbox) */}
        {/* NYHA (Checkbox Group) */}
        {renderCheckboxGroup(formData, handleCheckboxChange, "nyha", "NYHA", ["I", "II", "III", "IV"], GetTranslation, "(pior dispneia ou classe funcional nas últimas 2 semanas)")}

        {/* CCS (Checkbox Group) */}
        {renderCheckboxGroup(formData, handleCheckboxChange, "ccs", "CCS", ["I", "II", "III", "IV"], GetTranslation, "(Classificação de angina da Canadian Cardiovascular Society)")}

        {/* EUROscore */}
        {renderRangeField(formData, setFormData, "euroscore", "EUROscore (%)", 0, 100, 2, GetTranslation, "(%, mortalidade)")}

        {/* STS score */}
        {renderRangeField(formData, setFormData, "sts_score", "STS score (%)", 0, 100, 2, GetTranslation, "(%, mortalidade)")}

        {/* Frail score */}
        {renderSelectField(formData, handleChange, "frail_score", "Frail score", [0, 1, 2, 3, 4, 5], GetTranslation, "(1 ponto para cada resposta positiva: Fadigado? Não consegue subir 1 lance de escadas? Não consegue caminhar um quarteirão? Possui mais de 5 doenças? Perdeu mais de 5% do peso nos últimos 6 meses?)")}

        <div className="col-12 d-flex justify-content-between">
          <Button label={GetTranslation("Voltar")} className="btn btn-secondary" onClick={handleBack} />
          <Button label={id ? GetTranslation("Update") : GetTranslation("Create")} className="btn btn-primary" type="submit" onClick={confirmSave} />
        </div>
      </form>
    </div>
  );
};

export default EscoresForm;